import type { FC } from 'react';

import Pagination from '@/common-components/Pagination';
import AiFlaggedList from '@/features/moderation/components/AiFlaggedList';
import AiListingDetailDrawer from '@/features/moderation/components/AiListingDetailDrawer';
import ModerationDetailDrawer from '@/features/moderation/components/ModerationDetailDrawer';
import ModerationMetrics from '@/features/moderation/components/ModerationMetrics';
import ModerationToolbar from '@/features/moderation/components/ModerationToolbar';
import ReportsList from '@/features/moderation/components/ReportsList';
import ShouttModerationList from '@/features/moderation/components/ShouttModerationList';
import { useModerationPage } from '@/features/moderation/hooks/useModerationPage.hook';
import type { ModerationTab } from '@/types/moderation.types';

const EMPTY_COPY: Record<ModerationTab, { icon: string; title: string; sub: string }> = {
  all: {
    icon: '📥',
    title: 'Queue is clear',
    sub: 'Campus Shoutts land here as students post them.',
  },
  ai_flagged: {
    icon: '🤖',
    title: 'Nothing AI-flagged',
    sub: 'Posts the spam filter flags will appear here for review.',
  },
  reported: {
    icon: '🚨',
    title: 'No reported Shoutts',
    sub: 'Posts reported by students will appear here.',
  },
  removed: {
    icon: '🗑️',
    title: 'No removed Shoutts',
    sub: 'Removed or rejected posts can be restored from here.',
  },
  reports: {
    icon: '🚩',
    title: 'No reports',
    sub: 'Reports filed against users, listings or Shoutts will appear here.',
  },
};

const ModerationPage: FC = () => {
  const vm = useModerationPage();

  const noun = vm.tab === 'reports' ? 'report' : vm.tab === 'ai_flagged' ? 'item' : 'Shoutt';
  const empty = EMPTY_COPY[vm.tab];
  const shownCount =
    vm.tab === 'reports'
      ? vm.reports.length
      : vm.tab === 'ai_flagged'
        ? vm.aiFlagged.length
        : vm.shoutts.length;
  const startIndex = vm.total === 0 ? 0 : (vm.page - 1) * 12 + 1;
  const endIndex = vm.total === 0 ? 0 : startIndex + shownCount - 1;

  return (
    <>
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-[22px] font-extrabold tracking-tight text-[var(--color-ink)]">
              Shoutt Moderation
            </h1>
            <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
              {vm.total.toLocaleString()} {noun}
              {vm.total === 1 ? '' : 's'}
              {vm.openReportsCount > 0 && vm.tab !== 'reports'
                ? ` · ${vm.openReportsCount} open report${vm.openReportsCount === 1 ? '' : 's'}`
                : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={vm.onRescan}
              disabled={vm.isRescanning}
              title="Re-run AI moderation over recent posts and listings"
              className="inline-flex cursor-pointer items-center gap-2 self-start rounded-lg border border-[var(--color-divider)] bg-white px-4 py-2 text-[13px] font-semibold text-[var(--color-ink-muted)] transition-all hover:bg-[var(--color-surface-muted)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              🤖 {vm.isRescanning ? 'Scanning…' : 'AI Auto-Moderate'}
            </button>
            <button
              type="button"
              disabled
              title="Coming soon"
              className="inline-flex items-center gap-2 self-start rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white opacity-60 shadow-[0_4px_14px_rgba(42,102,217,0.3)]"
            >
              ✓ Approve All Safe
            </button>
          </div>
        </header>

        <div>
          <ModerationMetrics
            queueTotal={vm.queueCount}
            reportedCount={vm.reportedCount}
            openReportsCount={vm.openReportsCount}
          />

          <ModerationToolbar
            tab={vm.tab}
            search={vm.search}
            campusId={vm.campusId}
            reportStatus={vm.reportStatus}
            campuses={vm.campuses}
            reportedCount={vm.reportedCount}
            openReportsCount={vm.openReportsCount}
            aiFlaggedCount={vm.aiFlaggedCount}
            onChangeTab={vm.onChangeTab}
            onChangeSearch={vm.onChangeSearch}
            onChangeCampus={vm.onChangeCampus}
            onChangeReportStatus={vm.onChangeReportStatus}
          />

          {vm.isLoading ? (
            <div className="flex min-h-64 items-center justify-center text-[13px] text-[var(--color-ink-muted)]">
              Loading {noun}s…
            </div>
          ) : vm.isError ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
              <p className="text-sm font-semibold text-[var(--color-danger-ink)]">
                Couldn't load {noun}s
              </p>
              <p className="text-xs text-[var(--color-ink-muted)]">
                {vm.errorMessage ?? 'Please try again.'}
              </p>
            </div>
          ) : vm.total === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--color-divider)] bg-white p-6 text-center">
              <span className="text-3xl">{empty.icon}</span>
              <p className="text-sm font-semibold text-[var(--color-ink)]">{empty.title}</p>
              <p className="max-w-md text-xs text-[var(--color-ink-muted)]">{empty.sub}</p>
            </div>
          ) : vm.tab === 'reports' ? (
            <ReportsList
              items={vm.reports}
              mutatingId={vm.mutatingId}
              onView={vm.onViewReport}
              onOpenResolve={vm.onOpenResolve}
            />
          ) : vm.tab === 'ai_flagged' ? (
            <AiFlaggedList
              items={vm.aiFlagged}
              mutatingId={vm.mutatingId}
              onView={vm.onViewAiFlagged}
              onDismiss={vm.onDismissAiFlag}
              onRemove={vm.onRemoveAiFlagged}
            />
          ) : (
            <ShouttModerationList
              items={vm.shoutts}
              mutatingId={vm.mutatingId}
              onView={vm.onViewShoutt}
              onApprove={vm.onApprove}
              onOpenReject={vm.onOpenReject}
              onRemove={vm.onRemove}
              onRestore={vm.onRestore}
            />
          )}

          <footer className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-[12px] text-[var(--color-ink-muted)]">
              {vm.total === 0
                ? `No ${noun}s to show`
                : `Showing ${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${vm.total.toLocaleString()} ${noun}${vm.total === 1 ? '' : 's'}`}
            </div>
            {vm.totalPages > 1 ? (
              <Pagination page={vm.page} totalPages={vm.totalPages} onChange={vm.onChangePage} />
            ) : null}
          </footer>
        </div>
      </div>

      <ModerationDetailDrawer
        selected={vm.selected}
        shouttDetail={vm.shouttDetail}
        isLoadingDetail={vm.isLoadingDetail}
        isErrorDetail={vm.isErrorDetail}
        mutatingId={vm.mutatingId}
        noteFormFor={vm.noteFormFor}
        noteDraft={vm.noteDraft}
        noteError={vm.noteError}
        onClose={vm.onCloseDetail}
        onApprove={vm.onApprove}
        onOpenReject={vm.onOpenReject}
        onRemove={vm.onRemove}
        onRestore={vm.onRestore}
        onOpenResolve={vm.onOpenResolve}
        onDismissAiFlag={vm.onDismissAiFlag}
        onRemoveAiFlagged={vm.onRemoveAiFlagged}
        onChangeNote={vm.onChangeNote}
        onCancelNote={vm.onCancelNote}
        onSubmitNote={vm.onSubmitNote}
      />

      <AiListingDetailDrawer
        item={vm.aiListingItem}
        detail={vm.aiListingDetail}
        gallery={vm.aiListingGallery}
        isLoadingDetail={vm.isLoadingAiListing}
        isErrorDetail={vm.isErrorAiListing}
        mutatingId={vm.mutatingId}
        onClose={vm.onCloseDetail}
        onDismiss={vm.onDismissAiFlag}
        onRemove={vm.onRemoveAiFlagged}
      />

    </>
  );
};

export default ModerationPage;
